import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { DIALOGS } from 'src/models/DialogModel';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel,
  EXTENSION,
  FORMAT,
  ORIENTATION,
} from 'src/models/DownloadModel';
import { useEffect, useState } from 'react';
import { closeDialog } from 'src/redux/actions/dialogActions';
import {
  postDownloadEntitesAction,
  postEmailEntitesAction,
  setFilters,
} from 'src/redux/actions/querybuilderActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM } from 'src/constants/Form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { t } from 'src/lib/language';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
}));

const DownloadAmbassadorDialog = () => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const opened =
    useSelector((state: Store) => state.dialog[DIALOGS.ambassadorDownload]?.opened || false);
  const fundraisindID =
    useSelector((state: Store) => state.dialog[DIALOGS.ambassadorDownload]?.payload || undefined);

  useEffect(() => {
    if (opened) {
      dispatch(setError({ [FORM.download]: {} }));
      dispatch(setFilters({
        entity: ENTITIES.ambassadors,
        filters: { and: [['ambassadors.event_id', '=', fundraisindID]] },
      }));
    }
  }, [opened, fundraisindID]);

  const [options, setOptions] = useState({
    file: 'ambassadors',
    orientation: ORIENTATION.portrait,
    format: FORMAT.pdf,
    delivery: DELIVERY_TYPE.email,
    export_to_grid: false,
  } as DownloadOptionsModel);

  const extension = options.format === FORMAT.csv ? EXTENSION.csv : EXTENSION.pdf;

  const handleCancel = () => {
    dispatch(closeDialog({ dialog: DIALOGS.ambassadorDownload }));
  };

  const handleDownload = () => {
    dispatch(setError({ [FORM.download]: {} }));
    if (!options.file) {
      dispatch(setError({
        [FORM.download]: {
          file: ['Required'],
        },
      }));
      return;
    }
    if (!options.file) {
      options.file = 'ambassadors';
    }
    options.file = `${options.file.split('.')[0]}.${extension}`;
    if (options.delivery === DELIVERY_TYPE.download) {
      options.all = true;
      dispatch(postDownloadEntitesAction(ENTITIES.ambassadors, options));
    }
    if (options.delivery === DELIVERY_TYPE.email) {
      options.all = true;
      dispatch(postEmailEntitesAction(ENTITIES.ambassadors, options));
    }
    dispatch(closeDialog({ dialog: DIALOGS.ambassadorDownload }));
  };

  const onChange = (value: Value, field: string) => {
    setOptions({
      ...options,
      [field]: value,
    });
  };

  return (
    <Dialog
      aria-labelledby="download-result"
      open={opened}>
      <DialogTitle id="download-result">Download Ambassador(s)</DialogTitle>
      <DialogContent dividers>
        <div style={{ width: '480px' }}>
          <div className={classes.row}>
            <FormTextField
              form={FORM.download}
              label="File Name"
              name="file"
              onChange={onChange}
              required
              value={options.file.split('.')[0]} />
            { `.${extension}` }
          </div>
          <FormControl component="fieldset" style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Format</FormLabel>
            <RadioGroup
              row
              aria-label="format"
              name="format"
              value={options.format || FORMAT.pdf}
              onChange={(e) => onChange(e.target.value, 'format')}>
              <FormControlLabel value={FORMAT.pdf} control={<Radio />} label={FORMAT.pdf} />
              <FormControlLabel value={FORMAT.csv} control={<Radio />} label={FORMAT.csv} />
            </RadioGroup>
          </FormControl>
          <div />
          <FormControl
            component="fieldset"
            style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Delivery Type</FormLabel>
            <RadioGroup
              row
              aria-label="Delivery Type"
              name="download"
              value={options.delivery || DELIVERY_TYPE.email}
              onChange={(e) => onChange(e.target.value, 'delivery')}>
              <FormControlLabel
                value={DELIVERY_TYPE.email}
                control={<Radio />}
                label="By Email" />
              <FormControlLabel
                value={DELIVERY_TYPE.download}
                control={<Radio />}
                label="Download" />
            </RadioGroup>
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          { t(lang, 'misc.cancel') }
        </Button>
        <Button onClick={handleDownload} color="primary">
          { t(lang, 'misc.download') }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadAmbassadorDialog;
